import React from 'react';
import { DollarSign, Clock, CheckCircle, XCircle, AlertTriangle, RefreshCw, CreditCard } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { Card } from '../components/common/Card';

export const RefundsPage: React.FC = () => {
  return (
    <PageLayout
      title="Refund Policy"
      subtitle="Last updated: August 17, 2025"
    >
      <div className="space-y-8 text-slate-700 dark:text-slate-300">
        
        {/* Quick Overview */}
        <Card className="p-6 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <div className="flex items-start space-x-4">
            <DollarSign className="h-8 w-8 text-green-600 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">
                Satisfaction Guarantee
              </h3>
              <p className="text-green-800 dark:text-green-200">
                We want you to be completely satisfied with SleepCalc Pro. If you're not happy with your subscription, we offer refunds under the conditions outlined below.
              </p>
            </div>
          </div>
        </Card>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">1. Refund Eligibility</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Card className="p-6 border-green-200 dark:border-green-800">
              <div className="flex items-center space-x-3 mb-4">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Eligible for Refund</h3>
              </div>
              <ul className="space-y-2 text-sm">
                <li>• Requests made within 30 days of purchase</li>
                <li>• Technical issues preventing app use</li>
                <li>• Accidental or unauthorized purchases</li>
                <li>• Subscription cancellation within 7 days</li>
                <li>• Service not as described</li>
                <li>• Billing errors or duplicate charges</li>
              </ul>
            </Card>

            <Card className="p-6 border-red-200 dark:border-red-800">
              <div className="flex items-center space-x-3 mb-4">
                <XCircle className="h-6 w-6 text-red-600" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Not Eligible for Refund</h3>
              </div>
              <ul className="space-y-2 text-sm">
                <li>• Requests made after 30 days</li>
                <li>• Change of mind after extended use</li>
                <li>• Violation of Terms of Service</li>
                <li>• Partial month refunds (except first 7 days)</li>
                <li>• Refunds for free trial periods</li>
                <li>• Third-party payment processor fees</li>
              </ul>
            </Card>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">2. Refund Timeframes</h2>
          
          <div className="space-y-4">
            <Card className="p-4">
              <div className="flex items-start space-x-3">
                <Clock className="h-6 w-6 text-blue-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">7-Day Money-Back Guarantee</h3>
                  <p className="text-sm mb-2">
                    New subscribers can request a full refund within 7 days of their first purchase, no questions asked.
                  </p>
                  <div className="text-xs bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
                    <strong>Processing Time:</strong> 1-2 business days
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-6 w-6 text-orange-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">30-Day Conditional Refund</h3>
                  <p className="text-sm mb-2">
                    Refunds may be granted within 30 days for technical issues, billing errors, or other valid reasons at our discretion.
                  </p>
                  <div className="text-xs bg-orange-50 dark:bg-orange-900/20 p-2 rounded">
                    <strong>Processing Time:</strong> 3-5 business days after approval
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">3. Refund Process</h2>
          
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="bg-blue-100 dark:bg-blue-900/20 rounded-full p-2">
                <span className="text-blue-800 dark:text-blue-200 font-bold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Submit Refund Request</h3>
                <p className="text-sm">
                  Contact our support team at <strong>refunds@sleepcalc.pro</strong> with your:
                </p>
                <ul className="text-sm mt-2 space-y-1">
                  <li>• PayPal transaction ID</li>
                  <li>• Account email address</li>
                  <li>• Reason for refund request</li>
                  <li>• Purchase date</li>
                </ul>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="bg-blue-100 dark:bg-blue-900/20 rounded-full p-2">
                <span className="text-blue-800 dark:text-blue-200 font-bold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Review Process</h3>
                <p className="text-sm">
                  Our team will review your request within 1-2 business days and may contact you for additional information if needed.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="bg-blue-100 dark:bg-blue-900/20 rounded-full p-2">
                <span className="text-blue-800 dark:text-blue-200 font-bold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Refund Processing</h3>
                <p className="text-sm">
                  If approved, refunds are processed through PayPal back to your original payment method. You'll receive a confirmation email with tracking details.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">4. PayPal Integration</h2>
          
          <Card className="p-6 bg-blue-50 dark:bg-blue-900/20">
            <div className="flex items-start space-x-4">
              <CreditCard className="h-8 w-8 text-blue-600 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">PayPal Compatibility</h3>
                <p className="text-blue-800 dark:text-blue-200 mb-4">
                  All SleepCalc Pro subscriptions are processed through PayPal's secure payment system. Our refund policy is fully compatible with PayPal's buyer protection policies.
                </p>
                
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-blue-900 dark:text-blue-100">PayPal Dispute Resolution</h4>
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      If you have issues with our refund process, you can also file a dispute directly through PayPal's Resolution Center.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-blue-900 dark:text-blue-100">Automatic Refund Processing</h4>
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      Approved refunds are automatically processed through PayPal's system, ensuring secure and fast transactions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">5. Subscription Cancellation vs. Refunds</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <RefreshCw className="h-6 w-6 text-purple-600" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Cancellation</h3>
              </div>
              <div className="space-y-3 text-sm">
                <p><strong>What it does:</strong> Stops future billing cycles</p>
                <p><strong>Access:</strong> Continues until current period ends</p>
                <p><strong>Timeline:</strong> Immediate effect for next billing</p>
                <p><strong>Method:</strong> Account settings or customer support</p>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <DollarSign className="h-6 w-6 text-green-600" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Refund</h3>
              </div>
              <div className="space-y-3 text-sm">
                <p><strong>What it does:</strong> Returns money for current period</p>
                <p><strong>Access:</strong> Immediate loss of Pro features</p>
                <p><strong>Timeline:</strong> Subject to refund policy terms</p>
                <p><strong>Method:</strong> Customer support request required</p>
              </div>
            </Card>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">6. Special Circumstances</h2>
          
          <div className="space-y-4">
            <Card className="p-4">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Technical Issues</h3>
              <p className="text-sm">
                If you experience technical problems that prevent you from using SleepCalc Pro, we will work to resolve the issue. If we cannot fix the problem within a reasonable timeframe, you may be eligible for a full refund regardless of the time since purchase.
              </p>
            </Card>

            <Card className="p-4">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Medical Circumstances</h3>
              <p className="text-sm">
                If medical circumstances prevent you from using the service, please contact our support team. We will review each case individually and may grant refunds outside of our standard policy.
              </p>
            </Card>

            <Card className="p-4">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Service Changes</h3>
              <p className="text-sm">
                If we make significant changes to the service that materially affect functionality, existing subscribers may request a refund within 30 days of the change notification.
              </p>
            </Card>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">7. Refund Methods and Timing</h2>
          
          <div className="space-y-4">
            <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-6">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Processing Times</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="font-medium">PayPal Balance</div>
                  <div className="text-slate-600 dark:text-slate-400">Instant to 30 minutes</div>
                </div>
                <div>
                  <div className="font-medium">Credit/Debit Cards</div>
                  <div className="text-slate-600 dark:text-slate-400">3-5 business days</div>
                </div>
                <div>
                  <div className="font-medium">Bank Accounts</div>
                  <div className="text-slate-600 dark:text-slate-400">3-5 business days</div>
                </div>
              </div>
            </div>

            <Card className="p-4">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Refund Currency</h3>
              <p className="text-sm">
                Refunds will be processed in the same currency as the original payment. If exchange rates have changed, the amount may differ slightly from what you originally paid.
              </p>
            </Card>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">8. Contact Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Refund Requests</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Email:</strong> refunds@sleepcalc.pro</p>
                <p><strong>Subject Line:</strong> "Refund Request - [Your Email]"</p>
                <p><strong>Response Time:</strong> 1-2 business days</p>
                <p><strong>Languages:</strong> English</p>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-3">General Support</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Email:</strong> support@sleepcalc.pro</p>
                <p><strong>For:</strong> Technical issues, account questions</p>
                <p><strong>Response Time:</strong> Within 24 hours</p>
                <p><strong>Hours:</strong> Monday-Friday, 9 AM - 6 PM EST</p>
              </div>
            </Card>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">9. Policy Updates</h2>
          <p className="mb-4">
            We may update this Refund Policy from time to time to reflect changes in our practices or applicable laws. We will notify subscribers of material changes via:
          </p>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li>Email notification to registered users</li>
            <li>In-app notification</li>
            <li>Update notice on our website</li>
          </ul>
          <p>
            Changes will not affect refund requests submitted before the policy update.
          </p>
        </section>

        <div className="border-t border-slate-200 dark:border-slate-700 pt-8 text-center">
          <Card className="p-6 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              Questions About Refunds?
            </h3>
            <p className="text-slate-600 dark:text-slate-300 mb-4">
              Our customer support team is here to help you with any refund-related questions or concerns.
            </p>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              We strive to resolve all refund requests fairly and promptly while maintaining the integrity of our service.
            </div>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};